import { useState } from 'react';
import { Link } from 'react-router';
import { Trash2, ArrowUpRight } from 'lucide-react';
import { Button, Input, MultiSelectModal, Select } from '@/components/ui';
import type {
  ProductEventEntryMode,
  ProductEventLinkInput,
} from '../hooks/useProductEventsMutation';
import { fetchEventOptions, resolveEventOption } from './eventSelectLoaders';

const ENTRY_MODE_OPTIONS: ReadonlyArray<{ value: ProductEventEntryMode; label: string }> = [
  { value: 'ORDER', label: '주문 기준' },
  { value: 'QUANTITY', label: '수량 기준' },
  { value: 'USER', label: '유저 기준' },
];

export interface EventLinkDraft extends ProductEventLinkInput {
  /** Display label resolved at selection time (raw eventId 미노출). */
  label: string;
}

export interface ProductEventsFieldProps {
  value: ReadonlyArray<EventLinkDraft>;
  disabled?: boolean;
  onChange: (links: EventLinkDraft[]) => void;
}

export const ProductEventsField = ({ value, disabled, onChange }: ProductEventsFieldProps) => {
  const [open, setOpen] = useState(false);

  const selectedIds = value.map((link) => link.eventId);

  const labelById = new Map<string, string>(value.map((link) => [link.eventId, link.label]));

  const handleApply = (nextIds: ReadonlyArray<string>, resolved: Map<string, string>) => {
    const next = nextIds.map<EventLinkDraft>((eventId) => {
      const existing = value.find((link) => link.eventId === eventId);
      if (existing) return existing;
      return {
        eventId,
        entryMode: 'ORDER',
        minOrderAmount: null,
        minQuantity: null,
        label: resolved.get(eventId) ?? labelById.get(eventId) ?? eventId,
      };
    });
    onChange(next);
  };

  const updateLink = (eventId: string, patch: Partial<EventLinkDraft>) => {
    onChange(value.map((link) => (link.eventId === eventId ? { ...link, ...patch } : link)));
  };

  const removeLink = (eventId: string) => {
    onChange(value.filter((link) => link.eventId !== eventId));
  };

  return (
    <div className="flex flex-col gap-3" data-testid="product-events-field">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[13px]" style={{ color: 'var(--content-muted)' }}>
          이 상품을 노출할 이벤트 캠페인을 선택하세요. 저장 시 연결이 갱신됩니다.
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => setOpen(true)}
          data-testid="product-events-field-trigger"
        >
          {value.length > 0 ? '이벤트 변경' : '이벤트 선택'}
        </Button>
      </div>

      {value.length === 0 ? (
        <div
          className="rounded-md py-6 text-center text-[13px]"
          style={{
            background: 'var(--surface-0)',
            border: '1px dashed var(--divider-strong)',
            color: 'var(--content-muted)',
          }}
        >
          연결된 이벤트가 없습니다.
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {value.map((link) => (
            <li
              key={link.eventId}
              className="grid items-end gap-3 rounded-md p-3"
              style={{
                background: 'var(--surface-1)',
                border: '1px solid var(--divider-strong)',
                gridTemplateColumns: 'minmax(0,1fr) 150px 160px auto',
              }}
            >
              <div className="flex min-w-0 flex-col gap-1">
                <span
                  className="text-[11px] font-medium uppercase tracking-wide"
                  style={{ color: 'var(--content-muted)' }}
                >
                  이벤트
                </span>
                <span
                  className="truncate text-[14px] font-semibold"
                  style={{ color: 'var(--content-strong)' }}
                  title={link.label}
                >
                  {link.label}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <label
                  className="text-[11px] font-medium uppercase tracking-wide"
                  style={{ color: 'var(--content-muted)' }}
                >
                  참여 기준
                </label>
                <Select
                  value={link.entryMode}
                  disabled={disabled}
                  onChange={(e) =>
                    updateLink(link.eventId, {
                      entryMode: e.target.value as ProductEventEntryMode,
                    })
                  }
                >
                  {ENTRY_MODE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex flex-col gap-1">
                {link.entryMode === 'ORDER' ? (
                  <>
                    <label
                      className="text-[11px] font-medium uppercase tracking-wide"
                      style={{ color: 'var(--content-muted)' }}
                    >
                      최소 주문금액
                    </label>
                    <Input
                      type="number"
                      min={0}
                      step="1"
                      inputMode="numeric"
                      placeholder="제한 없음"
                      value={link.minOrderAmount ?? ''}
                      disabled={disabled}
                      onChange={(e) => {
                        const next = e.target.valueAsNumber;
                        updateLink(link.eventId, {
                          minOrderAmount: Number.isFinite(next) ? next : null,
                        });
                      }}
                    />
                  </>
                ) : link.entryMode === 'QUANTITY' ? (
                  <>
                    <label
                      className="text-[11px] font-medium uppercase tracking-wide"
                      style={{ color: 'var(--content-muted)' }}
                    >
                      최소 수량
                    </label>
                    <Input
                      type="number"
                      min={0}
                      step="1"
                      inputMode="numeric"
                      placeholder="제한 없음"
                      value={link.minQuantity ?? ''}
                      disabled={disabled}
                      onChange={(e) => {
                        const next = e.target.valueAsNumber;
                        updateLink(link.eventId, {
                          minQuantity: Number.isFinite(next) ? next : null,
                        });
                      }}
                    />
                  </>
                ) : (
                  <span className="text-[12px]" style={{ color: 'var(--content-soft)' }}>
                    유저당 1회 응모
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {/* QA-141 — 응모현황 라우팅 버그 수정.
                    종전엔 raw <a target="_blank"> 새 탭으로 열어, 새 탭의 cold-start 인증
                    레이스(메모리 토큰 부재 → rbacLoader 의 silent re-auth 경합)로
                    /dashboard?denied=1 로 튀는 현상이 있었다. 같은 탭 SPA 네비게이션(Link)으로
                    전환하면 메모리 토큰을 그대로 들고 가 가드가 통과하므로 대시보드 바운스가 사라진다.
                    eventId 가 비면(/cms/events//applications 방지) 링크를 렌더하지 않는다(be-o 우려 가드). */}
                {link.eventId ? (
                  <Link
                    to={`/cms/events/${link.eventId}/applications`}
                    className="inline-flex items-center gap-1 rounded px-2 py-1 text-[12px] focus-visible:outline-none focus-visible:ring-2"
                    style={{ color: 'var(--accent-default)' }}
                    title="이 이벤트의 응모현황을 봅니다"
                  >
                    응모현황
                    <ArrowUpRight size={12} aria-hidden="true" />
                  </Link>
                ) : null}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={disabled}
                  onClick={() => removeLink(link.eventId)}
                  aria-label="이벤트 연결 해제"
                >
                  <Trash2 size={14} style={{ color: 'var(--signal-danger)' }} />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <EventSelectModal
        open={open}
        onOpenChange={setOpen}
        initialValue={selectedIds}
        onApply={handleApply}
      />
    </div>
  );
};

interface EventSelectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialValue: ReadonlyArray<string>;
  onApply: (ids: ReadonlyArray<string>, resolved: Map<string, string>) => void;
}

const EventSelectModal = ({ open, onOpenChange, initialValue, onApply }: EventSelectModalProps) => {
  // resolveOption 결과를 라벨 캐시로 누적해 apply 시 라벨까지 전달.
  const resolvedLabels = new Map<string, string>();

  const resolveOption = async (eventId: string) => {
    const option = await resolveEventOption(eventId);
    if (option) resolvedLabels.set(eventId, option.label);
    return option;
  };

  return (
    <MultiSelectModal
      open={open}
      onOpenChange={onOpenChange}
      loadOptions={async (keyword, page) => {
        const result = await fetchEventOptions(keyword, page);
        result.items.forEach((item) => resolvedLabels.set(item.value, item.label));
        return result;
      }}
      resolveOption={resolveOption}
      initialValue={initialValue}
      onApply={(ids) => onApply(ids, resolvedLabels)}
      title="이벤트 선택"
      description="이 상품을 노출할 이벤트를 다중 선택하세요."
      searchPlaceholder="이벤트명으로 검색"
      applyLabel="선택 적용"
      emptySelectionHint="미선택 시 연결된 이벤트가 모두 해제됩니다."
    />
  );
};
